import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { Users, DollarSign, TrendingUp, LogOut, Crown, Wallet, ArrowUpCircle, History, CheckCircle2, XCircle, Clock, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * StaffPortalPage — the referral-agent's cockpit.
 *
 * Surfaces:
 *   • Lifetime earnings balance (USD) with a clear Withdrawable label.
 *   • Live-insights KPIs (today / yesterday / this week / this month / all)
 *     mirroring the main-admin Live Insights rhythm.
 *   • Referred-players list with their first-deposit status + earned commission.
 *   • Recent commission + withdrawal ledger (audit-friendly history).
 *   • Withdrawal form: amount + QR upload (file picker or URL paste) + note.
 *   • Withdrawal history with status badges and admin notes.
 */
export default function StaffPortalPage() {
  const { user, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);       // /api/referral-staff/me/dashboard
  const [earnings, setEarnings] = useState(null);         // /api/referral-staff/me/earnings
  const [withdrawals, setWithdrawals] = useState([]);     // /api/referral-staff/me/withdrawals
  const [loading, setLoading] = useState(true);
  const [showWd, setShowWd] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [d, e, w] = await Promise.all([
        api.get('/api/staff-portal/me').catch(() => ({ data: null })),
        api.get('/api/referral-staff/me/earnings'),
        api.get('/api/referral-staff/me/withdrawals'),
      ]);
      setDashboard(d.data);
      setEarnings(e.data);
      setWithdrawals(w.data?.withdrawals || []);
    } catch (err) {
      console.error('Staff portal error:', err);
      toast.error('Failed to load portal data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0E14' }}>
      <div className="spinner" />
    </div>
  );

  const summary = dashboard?.summary || {};
  const players = dashboard?.players || [];
  const staff = dashboard?.staff || earnings?.staff || {};
  const kpis = earnings?.kpis || {};
  const balance = earnings?.balance_usd ?? 0;

  return (
    <div className="min-h-screen" style={{ background: '#0B0E14' }} data-testid="staff-portal-page">
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Crown className="w-6 h-6 text-[#D4AF37]" />
          <h1 className="text-xl font-bold text-white">Referral Staff Portal</h1>
          <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-1 rounded-full" data-testid="staff-ref-code">
            {staff.referral_code || '—'}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-400 hidden sm:inline">Welcome, {staff.username || user?.username}</span>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="staff-logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Earnings hero — side-by-side Balance + Withdraw CTA */}
        <div className="rounded-2xl p-5 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.10), rgba(99,102,241,0.12))', border: '1px solid rgba(212,175,55,0.25)' }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="w-4 h-4 text-[#F5D76E]" />
                <p className="text-xs uppercase tracking-[0.14em] font-bold text-[#D4AF37]">Earnings Balance</p>
              </div>
              <p className="text-5xl font-bold tabular-nums" style={{ color: '#F5D76E' }} data-testid="staff-balance-usd">
                {fmtUSD(balance)}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Lifetime: {fmtUSD(earnings?.lifetime_credits_usd)} earned · {fmtUSD(earnings?.lifetime_debits_usd)} withdrawn
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowWd(true)}
                disabled={balance <= 0}
                className="bg-[#D4AF37] hover:bg-[#F5D76E] text-black font-bold"
                data-testid="staff-open-withdraw-btn"
              >
                <ArrowUpCircle className="w-4 h-4 mr-2" /> Request Withdrawal
              </Button>
            </div>
          </div>
        </div>

        {/* KPIs — today / yesterday / week / month / all */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="staff-kpis">
          {[
            { key: 'today', label: 'Today' },
            { key: 'yesterday', label: 'Yesterday' },
            { key: 'week', label: 'This Week' },
            { key: 'month', label: 'This Month' },
            { key: 'all', label: 'All Time' },
          ].map(p => {
            const k = kpis[p.key] || {};
            return (
              <div key={p.key} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid={`staff-kpi-${p.key}`}>
                <p className="text-[10px] uppercase tracking-wide text-gray-500">{p.label}</p>
                <p className="text-lg font-bold text-[#F5D76E] tabular-nums">{fmtUSD(k.earned_usd)}</p>
                <p className="text-[10px] text-gray-400">{k.first_deposit_events || 0} first deps · withdrawn {fmtUSD(k.withdrawn_usd)}</p>
              </div>
            );
          })}
        </div>

        {/* Summary tiles from legacy dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Tile icon={Users} iconColor="text-blue-400" iconBg="bg-blue-500/20" label="Referred Players" value={summary.referred_players || 0} testid="referred-players-card" />
          <Tile icon={TrendingUp} iconColor="text-green-400" iconBg="bg-green-500/20" label="First-Time Deposits" value={summary.first_time_deposit_count || 0} testid="first-deposit-count-card" />
          <Tile icon={DollarSign} iconColor="text-[#D4AF37]" iconBg="bg-[#D4AF37]/20" label="First-Time Deposit Total" value={fmtUSD(summary.first_time_deposit_total)} testid="first-deposit-total-card" />
        </div>

        {/* Referred Players Table */}
        <div className="glass-card rounded-xl p-5">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" /> Your Referred Players
          </h2>
          {players.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No referred players yet. Share your code <span className="text-[#F5D76E] font-mono">{staff.referral_code}</span> to get started.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="referred-players-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2.5 px-3 text-xs text-gray-400 font-semibold uppercase">Player</th>
                    <th className="text-left py-2.5 px-3 text-xs text-gray-400 font-semibold uppercase">Joined</th>
                    <th className="text-right py-2.5 px-3 text-xs text-gray-400 font-semibold uppercase">First Deposit</th>
                    <th className="text-right py-2.5 px-3 text-xs text-gray-400 font-semibold uppercase">Your Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((p) => {
                    const firstDep = Number(p.first_deposit_usd ?? p.first_deposit_sc ?? 0);
                    const commission = Number(p.commission_usd ?? 0);
                    return (
                      <tr key={p.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`referred-player-row-${p.id}`}>
                        <td className="py-2.5 px-3">
                          <p className="text-white font-medium">{p.username}</p>
                          <p className="text-[10px] text-gray-500">{p.email}</p>
                        </td>
                        <td className="py-2.5 px-3 text-gray-400 text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}</td>
                        <td className="py-2.5 px-3 text-right tabular-nums">
                          {firstDep > 0 ? <span className="text-green-400 font-semibold">{fmtUSD(firstDep)}</span> : <span className="text-gray-600 text-xs">Not deposited</span>}
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums">
                          {commission > 0 ? <span className="text-[#F5D76E] font-bold">+{fmtUSD(commission)}</span> : <span className="text-gray-600">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Withdrawals */}
        <WithdrawalsList withdrawals={withdrawals} />
      </main>

      {showWd && (
        <WithdrawModal
          balance={balance}
          onClose={() => setShowWd(false)}
          onSuccess={() => { setShowWd(false); fetchAll(); }}
        />
      )}
    </div>
  );
}


function Tile({ icon: Icon, iconColor, iconBg, label, value, testid }) {
  return (
    <div className="glass-card rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <span className="text-sm text-gray-400">{label}</span>
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
    </div>
  );
}


function WithdrawalsList({ withdrawals }) {
  if (!withdrawals.length) {
    return (
      <div className="glass-card rounded-xl p-5">
        <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <History className="w-5 h-5" /> Withdrawal History
        </h2>
        <p className="text-gray-500 text-center py-6 text-sm">No withdrawals yet. Request one when you're ready to cash out.</p>
      </div>
    );
  }
  const badge = (s) => {
    if (s === 'approved') return { bg: 'bg-green-500/20', fg: 'text-green-400', icon: CheckCircle2, label: 'Approved' };
    if (s === 'rejected') return { bg: 'bg-red-500/20', fg: 'text-red-400', icon: XCircle, label: 'Rejected' };
    return { bg: 'bg-yellow-500/20', fg: 'text-yellow-400', icon: Clock, label: 'Pending' };
  };
  return (
    <div className="glass-card rounded-xl p-5" data-testid="staff-withdrawals-list">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <History className="w-5 h-5" /> Withdrawal History
      </h2>
      <div className="space-y-2">
        {withdrawals.map(w => {
          const b = badge(w.status);
          return (
            <div key={w.id} className="rounded-lg p-3 flex items-center justify-between" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid={`withdrawal-row-${w.id}`}>
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${b.bg}`}>
                  <b.icon className={`w-4 h-4 ${b.fg}`} />
                </div>
                <div>
                  <p className="text-white font-bold tabular-nums">{fmtUSD(w.amount_usd)}</p>
                  <p className="text-[11px] text-gray-500">{fmtDate(w.created_at)} {w.decided_at ? `· decided ${fmtDate(w.decided_at)}` : ''}</p>
                  {w.admin_note && <p className="text-[11px] text-gray-400 mt-0.5 italic">“{w.admin_note}”</p>}
                </div>
              </div>
              <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded-full ${b.bg} ${b.fg}`}>{b.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function WithdrawModal({ balance, onClose, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [qrImage, setQrImage] = useState('');
  const [qrPreview, setQrPreview] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const pickFile = () => fileRef.current?.click();
  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Sanity cap at 2MB — QR images are small.
    if (f.size > 2 * 1024 * 1024) {
      toast.error('QR image too large (max 2MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result || '');
      setQrImage(url);
      setQrPreview(url);
    };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    if (amt > balance) return toast.error(`Max you can withdraw: ${fmtUSD(balance)}`);
    if (!qrImage) return toast.error('Attach your QR code image');
    setSubmitting(true);
    try {
      await api.post('/api/referral-staff/me/withdrawals', {
        amount_usd: amt,
        qr_image: qrImage,
        note: note || null,
      });
      toast.success('Withdrawal request submitted — admin will review shortly.');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || 'Failed to submit withdrawal');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl p-5 space-y-3" style={{ background: '#13111C', border: '1px solid rgba(212,175,55,0.3)' }} onClick={e => e.stopPropagation()} data-testid="staff-withdraw-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <ArrowUpCircle className="w-5 h-5 text-[#D4AF37]" /> Request Withdrawal
          </h3>
          <button className="text-gray-400 hover:text-white text-xl" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Available balance</p>
          <p className="text-2xl font-bold text-[#F5D76E] tabular-nums">{fmtUSD(balance)}</p>
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Amount (USD)</label>
          <Input
            type="number"
            min="0.01"
            max={balance}
            step="0.01"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder={`Up to ${fmtUSD(balance)}`}
            className="bg-[#0B0E14] border-white/10 text-white"
            data-testid="staff-wd-amount"
          />
          <div className="flex gap-1 mt-1.5">
            {[25, 50, 100].map(pct => (
              <button key={pct} onClick={() => setAmount(((balance * pct) / 100).toFixed(2))} className="text-[10px] px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-gray-300" data-testid={`staff-wd-pct-${pct}`}>
                {pct}%
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Attach QR / Payout proof</label>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="staff-wd-file-input" />
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={pickFile} className="flex-1 border-white/10" data-testid="staff-wd-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> Upload image
            </Button>
            {qrPreview && <img src={qrPreview} alt="QR preview" className="w-14 h-14 rounded-lg object-cover border border-white/10" />}
          </div>
          <p className="text-[10px] text-gray-500 mt-1">Or paste a direct URL:</p>
          <Input
            value={qrImage.startsWith('data:') ? '' : qrImage}
            onChange={e => { setQrImage(e.target.value); setQrPreview(e.target.value); }}
            placeholder="https://…"
            className="bg-[#0B0E14] border-white/10 text-white text-xs mt-1"
            data-testid="staff-wd-qr-url"
          />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Note (optional)</label>
          <Textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="e.g. UPI / Bank / TRC20 wallet"
            className="bg-[#0B0E14] border-white/10 text-white text-sm"
            data-testid="staff-wd-note"
          />
        </div>
        <div className="flex gap-2 pt-1">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/10" data-testid="staff-wd-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="flex-1 bg-[#D4AF37] hover:bg-[#F5D76E] text-black font-bold" data-testid="staff-wd-submit">
            {submitting ? 'Submitting…' : 'Submit Request'}
          </Button>
        </div>
      </div>
    </div>
  );
}
